/**
 * HustleMeetupsVerifiedMembersPage.jsx — /verified-members
 *
 * Long-form public trust page explaining why HustleMeetups verifies
 * its members. Linked from the shared HustleMeet header nav on the
 * Meet, Circles and Matchmaker sales pages.
 */
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ShieldCheck, ArrowRight, CheckCircle2 } from "lucide-react";
import PlatformHeader from "@/components/PlatformHeader";
import SuiteFooter from "@/components/SuiteFooter";
import { Button } from "@/components/ui/button";
import { hm } from "@/lib/hmCopy";

/* ── Content data — single source, easy to translate later ─────── */
const SECTIONS = [
  {
    h: "Real Meetups Should Start With Real People",
    p: [
      "HustleMeetups is built around a simple idea: if you are going to meet someone in the real world, you should have greater confidence that the person behind the profile is real.",
      "That is why HustleMeetups is being built as a community of verified members. Whether you are attending a professional networking event, joining a restaurant group, meeting people for coffee, attending live music, taking part in a walking group or joining an online business discussion, trust matters.",
      "HustleMeetups is designed to help real people build real connections. Verification is an important part of making that possible.",
    ],
  },
  {
    h: "What Is a Verified HustleMeetups Member?",
    p: [
      "A verified member is someone who has completed HustleMeetups' required membership verification process before receiving full access to participating in the community.",
      "The purpose is straightforward. We want to reduce:",
    ],
    bullets: [
      "fake profiles",
      "automated accounts",
      "spam accounts",
      "duplicate identities",
      "people deliberately pretending to be somebody else",
      "and other behaviour that can undermine trust inside online communities.",
    ],
    pAfter: [
      "Verification doesn't mean every member will agree with each other. It doesn't mean every meetup will lead to friendship. And it can never guarantee another person's behaviour.",
      "What it does provide is an additional level of accountability before people begin meeting one another through HustleMeetups. We believe that matters.",
    ],
  },
  {
    h: "Why Does HustleMeetups Verify Members?",
    p: [
      "Most social networks were created primarily for online interaction. HustleMeetups is different. Our goal is to help people move from discovering a group online to actually meeting other members.",
      "That might mean sitting around a restaurant table. Meeting in a hotel conference room. Having coffee with a local group. Walking through a park. Attending a music event. Joining a professional workshop. Or participating in an online video meeting before eventually meeting in person.",
      "When a platform is designed to bring people together, there should be a stronger focus on trust. That is why verified membership sits at the centre of HustleMeetups.",
    ],
  },
  {
    h: "A Better Starting Point for Meeting New People",
    p: [
      "Meeting someone new always involves a degree of trust. You may have spoken online. You may share an interest. You might belong to the same professional group. But until you actually meet, you probably know relatively little about each other.",
      "HustleMeetups verification is intended to provide members with a better starting point. Instead of an entirely anonymous profile being able to join the community without checks, members go through the HustleMeetups verification process. That helps create a community where participation is connected to a real person.",
      "It doesn't remove the need for normal common sense. But it can reduce some of the anonymity that has become common across the internet.",
    ],
  },
  {
    h: "Why Verification Matters for Professional Meetups",
    p: [
      "Professional relationships depend heavily on trust. If you are attending a meetup for:",
    ],
    bullets: [
      "business networking",
      "entrepreneurship",
      "technology",
      "marketing",
      "property",
      "professional services",
      "local business",
      "startups",
      "investment",
      "industry development",
      "or professional education,",
    ],
    pAfter: [
      "you are often introducing yourself professionally as well as personally. You may exchange business information. You may discuss opportunities. You might eventually refer clients, collaborate on a project or form a commercial relationship.",
      "Knowing that HustleMeetups operates as a verified-member community provides another layer of confidence when those conversations begin.",
      "The objective is not to make professional networking formal or difficult. Quite the opposite. The objective is to make it easier to walk into a room and start a conversation knowing that the group exists inside a community built around genuine membership.",
    ],
  },
  {
    h: "Why Verification Matters for Social Meetups",
    p: [
      "The same principle applies socially. Imagine joining a dinner group where twelve people are meeting at a restaurant. Or attending a piano bar with a music group. Joining five people for coffee. Meeting a walking group in a public park. Going to a local event with people you have never met before.",
      "These are the kinds of experiences HustleMeetups is designed to encourage. But we also recognise that meeting new people requires confidence.",
      "A verified-member community doesn't eliminate risk, and it should never replace sensible personal safety precautions. However, it creates an additional layer between an anonymous online account and participation in real-world groups. For HustleMeetups, that is an important distinction.",
    ],
  },
  {
    h: "Does Verified Mean Safe?",
    p: [
      "Verification helps establish that a membership relates to a genuine person. It should never be interpreted as a guarantee of someone's character, intentions or future behaviour.",
      "HustleMeetups members should continue to use the same sensible precautions they would use when meeting anyone new. For example: meet in appropriate public places when meeting someone for the first time, tell someone where you are going when appropriate, use your judgement, respect personal boundaries, and report behaviour that concerns you.",
      "Verification is one layer of community trust. Good judgement and good community standards remain equally important.",
    ],
  },
  {
    h: "A Community With Greater Accountability",
    p: [
      "One of the problems with anonymous online communities is that people can sometimes behave very differently behind an anonymous profile. When participation is connected more closely to a genuine identity, there is naturally greater accountability.",
      "That matters to:",
    ],
    bullets: [
      "members",
      "group organisers",
      "professional hosts",
      "businesses",
      "venues",
      "and the broader HustleMeetups community.",
    ],
    pAfter: [
      "Our goal is to create an environment where people feel comfortable introducing themselves, contributing to groups and attending meetups. A trusted community begins with knowing there are real people behind the profiles.",
    ],
  },
  {
    h: "Better Groups Start With Better Membership",
    p: [
      "Verification isn't only valuable to individual members. It can also help group organisers.",
      "Imagine organising a professional dinner for fifteen people. Or booking a hotel meeting room. Or arranging a private restaurant table. Or planning a group activity with limited capacity. As an organiser, you want genuine members joining your group and reserving places.",
      "Verified membership can help reduce low-quality participation from fake or automated accounts and support stronger communities over time. The result should be groups built around participation rather than meaningless membership numbers.",
      "A group of twenty genuine people who regularly meet can be far more valuable than a group showing thousands of anonymous profiles that never attend anything. HustleMeetups is interested in real participation.",
    ],
  },
  {
    h: "Verified Doesn't Mean Everyone Has to Be Public",
    p: [
      "Verification and privacy are not the same thing. A person should not have to publish every personal detail to other members simply because HustleMeetups verifies membership.",
      "The purpose of verification is to help HustleMeetups establish genuine membership. It does not mean every verification detail should automatically become visible publicly.",
      "HustleMeetups should only display member information that is appropriate for the community and consistent with its privacy policies and member settings. Trust should not require unnecessary exposure of personal information.",
    ],
  },
  {
    h: "Why Not Allow Anonymous Membership?",
    p: [
      "Anonymous participation has an important place on some parts of the internet. But HustleMeetups is primarily designed around people eventually meeting. That changes the equation.",
      "When someone joins a professional breakfast, social dinner, café meetup, music event or local walking group, they are stepping out from behind the screen. We believe the platform supporting those connections should encourage genuine participation from the beginning.",
      "That is why HustleMeetups has chosen verified membership as part of its community model.",
    ],
  },
  {
    h: "Verification Helps Protect the Value of the Community",
    p: [
      "A community becomes less useful when members have to constantly question whether accounts are real. Spam damages conversations. Fake profiles reduce confidence. Automated accounts create noise. Fraudulent identities can make people reluctant to participate.",
      "Over time, these things damage the value of a platform. HustleMeetups wants to build in the opposite direction. Smaller amounts of noise. Greater accountability. More useful groups. More genuine attendance. More real conversations. And ultimately, stronger communities.",
    ],
  },
  {
    h: "Does HustleMeetups Verify Every Member?",
    p: [
      "HustleMeetups is intended to operate as a verified-member community. Members must complete the required verification process applicable to their account before receiving the relevant access to participate in HustleMeetups.",
      "The exact verification methods may evolve as the platform grows and as better technologies become available. The principle, however, remains the same: HustleMeetups is for real people who want to connect with real people.",
    ],
  },
  {
    h: "Can Verification Prevent Fake Accounts Completely?",
    p: [
      "No online verification system should claim to be perfect. Technology changes. People attempt to circumvent systems. No responsible platform should promise that verification completely removes every possible bad actor.",
      "The purpose of HustleMeetups verification is to make fake or misleading participation more difficult and to create greater accountability than an unrestricted anonymous membership model. It is one component of a broader approach to community trust.",
    ],
  },
  {
    h: "What Happens if a Member Behaves Inappropriately?",
    p: [
      "Verified membership does not give anyone permission to behave badly. Members remain responsible for their conduct.",
      "HustleMeetups should maintain community standards covering inappropriate behaviour, harassment, misleading information, spam, abuse and other conduct that can damage the community. Members should also be able to report concerns to HustleMeetups.",
      "Depending on the circumstances, action could include warnings, restrictions, suspension or removal from the platform in accordance with HustleMeetups' terms and community policies. Verification makes that accountability more meaningful because membership is designed to relate to genuine participants.",
    ],
  },
  {
    h: "Can Businesses and Professionals Be Verified Too?",
    p: [
      "HustleMeetups can support both individual and professional participation. A professional attending a business meetup may participate as an individual while also identifying their business, profession or organisation where appropriate.",
      "Businesses hosting or participating in meetups may also have relevant business information associated with their presence on the platform.",
      "The objective is transparency without making the platform unnecessarily complicated. Members should be able to understand who they are meeting and why they are participating.",
    ],
  },
  {
    h: "Why HustleMeetups Is Different",
    p: [
      "Most platforms focus on how many people can join. HustleMeetups wants to focus on how many genuine connections can be created. That means measuring success differently.",
      "Not just:",
    ],
    bullets: [
      "members",
      "followers",
      "likes",
      "views",
      "or online engagement.",
    ],
    pAfter: [
      "But:",
    ],
    bullets2: [
      "people attending",
      "groups meeting",
      "conversations happening",
      "business relationships beginning",
      "friendships forming",
      "venues being discovered",
      "and communities becoming stronger.",
    ],
    pAfter2: [
      "Verification supports that mission because there is a real person behind each genuine connection.",
    ],
  },
  {
    h: "Real People Make Better Communities",
    p: [
      "Technology can introduce people. It can recommend groups. It can organise events. It can manage RSVPs. It can help people discover local venues. It can even help match people based on interests.",
      "But technology shouldn't become the relationship itself. The best part still happens when two people say: \u201CHi, nice to meet you.\u201D",
      "That's why HustleMeetups exists. We want technology to make genuine human connection easier. And we believe genuine connection starts with genuine people.",
    ],
  },
  {
    h: "Join a Community Built Around Real People",
    p: [
      "Whether you're looking for:",
    ],
    bullets: [
      "professional networking",
      "business relationships",
      "new friends",
      "social groups",
      "dinner companions",
      "live music",
      "coffee meetups",
      "walking groups",
      "local activities",
      "shared interests",
      "online discussions",
      "or simply more reasons to get out and meet people,",
    ],
    pAfter: [
      "HustleMeetups gives you somewhere to start. Find groups that interest you. Join the community. Choose the meetups you want to attend. Meet people. Build relationships. And do it as part of a community where membership begins with verification.",
    ],
  },
];

/* ── Meta helper — set title + description tag ─────────────────── */
function useMeta(title, description) {
  useEffect(() => {
    document.title = title;
    let tag = document.querySelector('meta[name="description"]');
    if (!tag) {
      tag = document.createElement("meta");
      tag.setAttribute("name", "description");
      document.head.appendChild(tag);
    }
    tag.setAttribute("content", description);
  }, [title, description]);
}

/* ── Bullet list ──────────────────────────────────────────────── */
function BulletList({ items, testId }) {
  return (
    <ul
      data-testid={testId}
      className="mt-3 space-y-2 text-sm md:text-base text-stone-700 dark:text-stone-300"
    >
      {items.map((it, i) => (
        <li key={i} className="flex items-start gap-2 leading-relaxed">
          <CheckCircle2
            size={16}
            className="text-amber-500 shrink-0 mt-1"
            aria-hidden="true"
          />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
}

/* ── Page ─────────────────────────────────────────────────────── */
export default function HustleMeetupsVerifiedMembersPage() {
  const { t, i18n } = useTranslation();

  useMeta(
    "Verified Members | Why HustleMeetups Verifies Every Member",
    "Learn why HustleMeetups is built around verified membership and how verification helps create more trusted professional, social and local meetup communities.",
  );

  useEffect(() => {
    document.documentElement.lang = (i18n.language || "en").split("-")[0];
  }, [i18n.language]);

  const HM_NAV = [
    { to: "/meet",             label: hm(t, "nav.hustlemeet") },
    { to: "/circles",     label: hm(t, "nav.circles") },
    { to: "/matchmaker",  label: hm(t, "nav.matchmaker") },
    { to: "/verified-members",       label: hm(t, "nav.verified") },
  ];

  return (
    <div
      data-testid="verified-members-page"
      className="min-h-screen bg-slate-50 text-stone-900 dark:bg-[#0a0908] dark:text-stone-100 transition-colors"
    >
      <PlatformHeader
        brand={{ name: hm(t, "nav.hustlemeet"), to: "/meet" }}
        nav={HM_NAV}
        showBookCta={false}
        showCart={false}
      />

      {/* Hero */}
      <section
        data-testid="verified-hero"
        className="relative overflow-hidden border-b border-stone-200 dark:border-stone-800"
        style={{ background: "linear-gradient(135deg,#0a0908 0%,#1c1917 55%,#7c2d12 120%)" }}
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 text-white">
          <p className="text-amber-400 text-[11px] font-black uppercase tracking-[0.32em] inline-flex items-center gap-2">
            <ShieldCheck size={14} aria-hidden="true" /> HustleMeetups · Community Trust
          </p>
          <h1
            data-testid="verified-hero-title"
            className="mt-3 text-4xl md:text-6xl font-black leading-[1.05] max-w-3xl"
          >
            Verified <span className="text-amber-400">Members</span>
          </h1>
          <p className="mt-4 text-lg md:text-2xl font-bold text-white/90 max-w-3xl">
            Why HustleMeetups Verifies Members
          </p>
          <p className="mt-5 text-base md:text-lg text-white/80 max-w-2xl leading-relaxed">
            Real Meetups should start with real people. Verification is how
            we protect that promise — for professional networking, social
            groups, and every conversation that happens off-screen.
          </p>
        </div>
      </section>

      {/* Article */}
      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        {SECTIONS.map((s, idx) => (
          <section
            key={idx}
            data-testid={`verified-section-${idx}`}
            className="mb-10"
          >
            <h2 className="text-2xl md:text-3xl font-black tracking-tight text-stone-900 dark:text-stone-50 mb-3">
              {s.h}
            </h2>
            {s.p.map((para, i) => (
              <p
                key={`p-${i}`}
                className="mt-3 text-sm md:text-base text-stone-700 dark:text-stone-300 leading-relaxed"
              >
                {para}
              </p>
            ))}
            {s.bullets && (
              <BulletList
                items={s.bullets}
                testId={`verified-section-${idx}-bullets`}
              />
            )}
            {s.pAfter && s.pAfter.map((para, i) => (
              <p
                key={`pa-${i}`}
                className="mt-3 text-sm md:text-base text-stone-700 dark:text-stone-300 leading-relaxed"
              >
                {para}
              </p>
            ))}
            {s.bullets2 && (
              <BulletList
                items={s.bullets2}
                testId={`verified-section-${idx}-bullets2`}
              />
            )}
            {s.pAfter2 && s.pAfter2.map((para, i) => (
              <p
                key={`pa2-${i}`}
                className="mt-3 text-sm md:text-base text-stone-700 dark:text-stone-300 leading-relaxed"
              >
                {para}
              </p>
            ))}
          </section>
        ))}

        {/* Signature block */}
        <section
          data-testid="verified-signature"
          className="mt-12 rounded-3xl border border-amber-300 dark:border-amber-800 bg-gradient-to-br from-amber-400/10 to-orange-500/10 p-8 md:p-12 text-center"
        >
          <p className="text-amber-500 text-[11px] font-black uppercase tracking-[0.32em]">
            HustleMeetups
          </p>
          <p className="mt-3 text-2xl md:text-4xl font-black leading-tight">
            Verified People. Real Groups. <span className="text-amber-500">Real Connections.</span>
          </p>
          <p className="mt-4 text-sm md:text-base text-stone-700 dark:text-stone-300 max-w-xl mx-auto">
            Because meeting people should start with knowing they're real.
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <Button
              asChild
              className="rounded-full bg-emerald-500 text-slate-950 hover:bg-emerald-400 font-black h-12 px-6"
              data-testid="verified-cta-join"
            >
              <a href="https://app.hustlemeetups.au/signup" rel="noopener">
                Join Hustle Membership <ArrowRight size={14} className="ml-1" aria-hidden="true" />
              </a>
            </Button>
            <Button
              asChild
              variant="outline"
              className="rounded-full border-amber-400 text-amber-600 hover:bg-amber-500/10 font-black h-12 px-6"
              data-testid="verified-cta-explore"
            >
              <Link to="/meet">Explore HustleMeet groups</Link>
            </Button>
          </div>
        </section>
      </article>

      <SuiteFooter />
    </div>
  );
}
